import React from 'react';
import { Box, Typography, Chip, Grid, Card, CardContent } from '@mui/material';
import { useNavigate } from 'react-router-dom';

function DefaultPlannerWelcome() {
    const navigate = useNavigate();

    const featureCategories = [
        {
            category: "🤖 AI & Command Center",
            items: [
                { title: 'Concierge', desc: 'AI-powered itinerary wizard & instant event recommendations', path: 'Concierge', icon: '🤖', color: '#ec4899' },
                { title: 'Details', desc: 'Set master settings, categories, and event permissions', path: 'Details', icon: '⚙️', color: '#6366f1' },
                { title: 'Map', desc: 'Interactive visual mapping for your entire journey', path: 'MapMobile', icon: '🗺️', color: '#3b82f6' },
                { title: 'Notes', desc: 'Central hub for master plans, scratchpads, and ideas', path: 'Notes', icon: '📝', color: '#8b5cf6' },
            ]
        },
        {
            category: "📅 Schedule & Logistics",
            items: [
                { title: 'Itinerary', desc: 'Day-by-day master timeline and drag-and-drop schedule', path: 'Itinerary', icon: '📅', color: '#3b82f6' },
                { title: 'Dates & Times', desc: 'Lock in event timelines, countdowns, and duration', path: 'Dates', icon: '⏰', color: '#f59e0b' },
                { title: 'Location', desc: 'Set destination pinpoints, addresses, and navigation', path: 'Location', icon: '📍', color: '#10b981' },
                { title: 'Weather', desc: 'Live forecasts & historic climate reports for your trip', path: 'Weather', icon: '🌤️', color: '#06b6d4' },
                { title: 'To-Do List', desc: 'Checklists, action items, and task assignments', path: 'Todo', icon: '✅', color: '#10b981' },
            ]
        },
        {
            category: "✈️ Travel & Stay",
            items: [
                { title: 'Lodging', desc: 'Hotels, rentals, confirmation codes, and check-ins', path: 'Lodging', icon: '🏨', color: '#f59e0b' },
                { title: 'Flights', desc: 'Flight tracker, routes, and boarding pass info', path: 'Flights', icon: '✈️', color: '#0284c7' },
                { title: 'Car Rentals', desc: 'Vehicle reservations, pickup spots, and rental specs', path: 'CarRentals', icon: '🚗', color: '#f43f5e' },
                { title: 'Cruises', desc: 'Cabin numbers, port schedules, and cruise itineraries', path: 'Cruises', icon: '🚢', color: '#0ea5e9' },
            ]
        },
        {
            category: "👥 Crew, Media & Expenses",
            items: [
                { title: 'Attendees', desc: 'Crew roster, rsvp status, roles, and guest profiles', path: 'Attendees', icon: '👥', color: '#8b5cf6' },
                { title: 'Messages', desc: 'Live group chat, announcements, and team thread', path: 'Messages', icon: '💬', color: '#ec4899' },
                { title: 'Activities', desc: 'Tours, excursions, nightlife, and booked events', path: 'Activities', icon: '🎟️', color: '#84cc16' },
                { title: 'Ideas', desc: 'Crowdsourced suggestions & bucket list wishlist', path: 'Ideas', icon: '💡', color: '#eab308' },
                { title: 'Meals', desc: 'Restaurant reservations, menus, and dining plans', path: 'Meals', icon: '🍽️', color: '#f97316' },
                { title: 'Supplies', desc: 'Gear lists, packing checklists, and shared items', path: 'Supplies', icon: '🎒', color: '#14b8a6' },
                { title: 'Photos', desc: 'Shared event photo albums and memory vault', path: 'Photos', icon: '📸', color: '#a855f7' },
                { title: 'Documents', desc: 'Passports, tickets, PDFs, and legal paperwork', path: 'Documents', icon: '📄', color: '#64748b' },
                { title: 'Receipts', desc: 'Expense tracking, receipt scans, and budget split', path: 'Receipts', icon: '🧾', color: '#22c55e' },
            ]
        }
    ];

    return (
        <Box
            sx={{
                animation: 'fadeIn 0.3s ease-in-out',
                p: { xs: 2, sm: 4 },
                height: '100%',
                maxHeight: 'calc(100vh - 70px)',
                overflowY: 'auto',
                boxSizing: 'border-box',
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: '10px' }
            }}
        >
            {/* Header / Hero Section */}
            <Box sx={{ textAlign: 'center', mb: 4, pt: 1 }}>
                <Chip
                    label="⚡ ULTIMATE EVENT CONTROL ⚡"
                    sx={{
                        bgcolor: 'rgba(220, 38, 38, 0.1)',
                        color: '#dc2626',
                        border: '1px solid #ef4444',
                        fontWeight: 900,
                        fontSize: '0.75rem',
                        letterSpacing: '1.5px',
                        mb: 2,
                        px: 1
                    }}
                />

                <Typography
                    variant="h3"
                    sx={{
                        fontWeight: 900,
                        mb: 1.5,
                        fontSize: { xs: '1.8rem', sm: '2.8rem' },
                        background: 'linear-gradient(135deg, #0f172a 0%, #f59e0b 50%, #dc2626 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        letterSpacing: '-0.5px',
                        textTransform: 'uppercase'
                    }}
                >
                    Everything You Need to Run an Epic Event
                </Typography>

                <Typography
                    variant="body1"
                    sx={{
                        color: '#64748b',
                        maxWidth: '680px',
                        mx: 'auto',
                        fontWeight: 600,
                        fontSize: { xs: '0.9rem', sm: '1.05rem' },
                        lineHeight: 1.6
                    }}
                >
                    From AI travel concierge and flight tracking to live crew messaging and expense splitting—pick a feature below to jump straight in!
                </Typography>
            </Box>

            {/* Feature Categories & Cards */}
            <Box sx={{ maxWidth: '900px', mx: 'auto', pb: 6 }}>
                {featureCategories.map((sec) => (
                    <Box key={sec.category} sx={{ mb: 4 }}>
                        <Typography
                            variant="overline"
                            sx={{
                                fontWeight: 900,
                                color: '#475569',
                                fontSize: '0.8rem',
                                letterSpacing: '1px',
                                display: 'block',
                                mb: 2,
                                borderBottom: '2px solid #f1f5f9',
                                pb: 0.5
                            }}
                        >
                            {sec.category}
                        </Typography>

                        <Grid container spacing={2}>
                            {sec.items.map((feat) => (
                                <Grid item xs={12} sm={6} md={3} key={feat.title}>
                                    <Card
                                        onClick={() => navigate(feat.path)}
                                        sx={{
                                            height: '100%',
                                            cursor: 'pointer',
                                            borderRadius: '16px',
                                            border: '1px solid #e2e8f0',
                                            bgcolor: '#ffffff',
                                            transition: 'all 0.2s ease-in-out',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: '0 12px 24px rgba(0,0,0,0.08)',
                                                borderColor: feat.color,
                                                bgcolor: '#fafafa'
                                            }
                                        }}
                                    >
                                        <CardContent sx={{ p: '16px !important' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                <Typography variant="h6" sx={{ fontSize: '1.3rem' }}>
                                                    {feat.icon}
                                                </Typography>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a', fontSize: '0.95rem' }}>
                                                    {feat.title}
                                                </Typography>
                                            </Box>

                                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.78rem', lineHeight: 1.4, display: 'block' }}>
                                                {feat.desc}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

export default DefaultPlannerWelcome;